import {
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

interface LabelledProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
}

function Labelled({
  label,
  error,
  hint,
  required,
  id,
  children,
}: LabelledProps & { id: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-danger ms-1">
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint ? <p className="text-xs text-text-muted">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const base = 'h-10 rounded-md border bg-surface px-3 text-base text-text';

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>, LabelledProps {
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string | undefined;
}

/** Native select: keyboard-complete and screen-reader-friendly without extra code (ponytail). */
export function SelectField({
  label,
  error,
  hint,
  options,
  placeholder,
  className = '',
  ...rest
}: SelectFieldProps) {
  const id = useId();
  return (
    <Labelled label={label} error={error} hint={hint} required={rest.required} id={id}>
      <select
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        className={`${base} ${error ? 'border-danger' : 'border-border'} ${className}`}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Labelled>
  );
}

export interface DateFieldProps extends LabelledProps {
  value: string;
  onChange: (iso: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  autoFocus?: boolean | undefined;
}

/** Native date input (ISO yyyy-mm-dd): Gregorian, locale-aware picker, no library (11 §2.3). */
export function DateField({
  label,
  error,
  hint,
  required,
  value,
  onChange,
  min,
  max,
  disabled,
  autoFocus,
}: DateFieldProps) {
  const id = useId();
  return (
    <Labelled label={label} error={error} hint={hint} required={required} id={id}>
      <input
        id={id}
        type="date"
        dir="ltr"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={`${base} num ${error ? 'border-danger' : 'border-border'}`}
      />
    </Labelled>
  );
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>, LabelledProps {}

export function TextAreaField({ label, error, hint, className = '', ...rest }: TextAreaFieldProps) {
  const id = useId();
  return (
    <Labelled label={label} error={error} hint={hint} required={rest.required} id={id}>
      <textarea
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        className={`min-h-20 rounded-md border bg-surface px-3 py-2 text-base ${error ? 'border-danger' : 'border-border'} ${className}`}
      />
    </Labelled>
  );
}

export interface NumberFieldProps extends LabelledProps {
  value: number;
  onChange: (n: number) => void;
  min?: number | undefined;
  max?: number | undefined;
  disabled?: boolean | undefined;
}

export function NumberField({
  label,
  error,
  hint,
  required,
  value,
  onChange,
  min,
  max,
  disabled,
}: NumberFieldProps) {
  const id = useId();
  return (
    <Labelled label={label} error={error} hint={hint} required={required} id={id}>
      <input
        id={id}
        type="number"
        dir="ltr"
        inputMode="numeric"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onChange(e.target.valueAsNumber)}
        aria-invalid={error ? true : undefined}
        className={`${base} num w-24 ${error ? 'border-danger' : 'border-border'}`}
      />
    </Labelled>
  );
}
